import { ApolloClient, InMemoryCache } from "@apollo/client";
import { CREATE_NEWS_GENERATION_RECORD_MUTATION } from "../mutations/createNewsGenerationRecordMutation";
import { UPDATE_NEWS_GENERATION_RECORD_MUTATION } from "../mutations/updateNewsGenerationRecordMutation";
import { DELETE_NEWS_GENERATION_RECORD_MUTATION } from "../mutations/deleteNewsGenerationRecordMutation";
import { GET_NEWS_GENERATION_RECORD_QUERY } from "../queries/getNewsGenerationRecordQuery";
import { LIST_NEWS_GENERATION_RECORDS_QUERY } from "../queries/listNewsGenerationRecordsQuery";

// News Generation GraphQL endpoint (different from queue services)
const NEWS_GENERATION_GRAPHQL_ENDPOINT =
  "https://vxrrj7fmh5cufbnqznkepzgnra.appsync-api.us-east-1.amazonaws.com/graphql";

// Initialize Apollo Client for news generation services
const newsGenerationClient = new ApolloClient({
  uri: NEWS_GENERATION_GRAPHQL_ENDPOINT,
  cache: new InMemoryCache(),
  headers: {
    "x-api-key": "da2-2nu76mkqx5ex5ojcz6p4ckcpaq",
    // Alternative header format if needed
    // "Authorization": "Bearer news-generation-api-key",
  },
});

/**
 * Test function to debug API connection issues
 * Call this to verify the API configuration
 */
export const testNewsGenerationApiConnection = async () => {
  console.group("🧪 Testing News Generation API Connection");

  console.log("📋 Configuration:");
  console.log(`  Endpoint: ${NEWS_GENERATION_GRAPHQL_ENDPOINT}`);
  console.log(`  API Key: news-generation-api-key`);

  try {
    // Test with a simple query to check connectivity
    const testInput = {
      name: "Test News Generation Record",
      status: "pending",
      data: '{"test": "news-generation-data"}',
    };

    console.log("🔄 Testing News GenerationAPI call...");
    const result = await createNewsGenerationRecordApi(testInput);

    console.log("✅ API call successful:", result);
    return { success: true, result };
  } catch (error) {
    console.error("❌ API call failed:", error);
    console.error("🔍 Error details:");
    console.error("  Message:", error.message);
    console.error("  GraphQL Errors:", error.graphQLErrors);
    console.error("  Network Error:", error.networkError);

    return { success: false, error };
  } finally {
    console.groupEnd();
  }
};

// API call to create a new news generation record
export const createNewsGenerationRecordApi = async (input) => {
  console.log(
    `PMM - SF - newsGenerationApi.js - createNewsGenerationRecordApi - input is: ${JSON.stringify(
      input
    )}`
  );

  console.log(
    `PMM - SF - newsGenerationApi.js - Using endpoint: ${NEWS_GENERATION_GRAPHQL_ENDPOINT}`
  );
  console.log(
    `PMM - SF - newsGenerationApi.js - Using API key: news-generation-api-key`
  );

  const apiPayload = {
    mutation: CREATE_NEWS_GENERATION_RECORD_MUTATION,
    variables: { input },
  };

  console.log(
    `PMM - SF - newsGenerationApi.js - createNewsGenerationRecordApi - apiPayload is: ${JSON.stringify(
      apiPayload
    )}`
  );

  try {
    const response = await newsGenerationClient.mutate(apiPayload);
    console.log(`PMM - SF - response is: ${JSON.stringify(response)}`);
    return response.data.createNewsArticle;
  } catch (error) {
    console.error(`Error creating news generation record: ${error.message}`);
    console.error(`Full error details:`, error);
    console.error(`GraphQL errors:`, error.graphQLErrors);
    console.error(`Network error:`, error.networkError);
    throw error;
  }
};

// API call to update an existing news generation record
export const updateNewsGenerationRecordApi = async (newsGenerationInput) => {
  console.log(
    `PMM - SF - newsGenerationApi.js - updateNewsGenerationRecordApi - newsGenerationInput is: ${JSON.stringify(
      newsGenerationInput
    )}`
  );

  const apiPayload = {
    mutation: UPDATE_NEWS_GENERATION_RECORD_MUTATION,
    variables: { input: newsGenerationInput },
  };

  try {
    const response = await newsGenerationClient.mutate(apiPayload);

    return response.data.updateNewsArticle;
  } catch (error) {
    console.error(`Error updating news generation record: ${error.message}`);
    console.error(`Full error details:`, error);
    console.error(`GraphQL errors:`, error.graphQLErrors);
    console.error(`Network error:`, error.networkError);
    throw error;
  }
};

// API call to delete a news generation record
export const deleteNewsGenerationRecordApi = async (newsId, tenantId) => {
  console.log(
    `PMM - SF - newsGenerationApi.js - deleteNewsGenerationRecordApi - newsId is: ${newsId}, tenantId is: ${tenantId}`
  );

  const apiPayload = {
    mutation: DELETE_NEWS_GENERATION_RECORD_MUTATION,
    variables: { newsId, tenantId },
  };

  try {
    const response = await newsGenerationClient.mutate(apiPayload);

    return response.data.deleteNewsArticle;
  } catch (error) {
    console.error(`Error deleting news generation record: ${error.message}`);
    console.error(`Full error details:`, error);
    console.error(`GraphQL errors:`, error.graphQLErrors);
    console.error(`Network error:`, error.networkError);
    throw error;
  }
};

// API call to get a single news generation record
export const getNewsGenerationRecordApi = async (newsId, tenantId) => {
  console.log(
    `PMM - SF - newsGenerationApi.js - getNewsGenerationRecordApi - newsId is: ${newsId}, tenantId is: ${tenantId}`
  );

  const apiPayload = {
    query: GET_NEWS_GENERATION_RECORD_QUERY,
    variables: { newsId, tenantId },
  };

  try {
    const response = await newsGenerationClient.query(apiPayload);

    return response.data.getNewsArticle;
  } catch (error) {
    console.error(`Error getting news generation record: ${error.message}`);
    console.error(`Full error details:`, error);
    console.error(`GraphQL errors:`, error.graphQLErrors);
    console.error(`Network error:`, error.networkError);
    throw error;
  }
};

// API call to list news generation records
export const listNewsGenerationRecordsApi = async (tenantId, status, limit) => {
  console.log(
    `PMM - SF - newsGenerationApi.js - listNewsGenerationRecordsApi - tenantId is: ${tenantId}, status is: ${status}, limit is: ${limit}`
  );

  const apiPayload = {
    query: LIST_NEWS_GENERATION_RECORDS_QUERY,
    variables: { tenantId, status, limit },
  };

  try {
    const response = await newsGenerationClient.query(apiPayload);

    return response.data.listNewsArticles;
  } catch (error) {
    console.error(`Error listing news generation records: ${error.message}`);
    console.error(`Full error details:`, error);
    console.error(`GraphQL errors:`, error.graphQLErrors);
    console.error(`Network error:`, error.networkError);
    throw error;
  }
};
